import { UpdateBasketStrategy } from '@epicuro-next/state/ubc-communication/ubc-basket';
import {
  getDeliveryShopId,
  getMergedBasketItemSource,
  isNonNullish,
} from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  BasketHeader,
  BasketUpdateItem,
  BasketUpdateRequest,
  OrderType,
} from 'graphql-api/baskets-api-interfaces';

import { BasketUpdateShops } from '../../models';
import { ExtendedBasketItem } from '../../state/reducers/basket-data.reducer';
import {
  bulkOrderEqualDates,
  futureOrderEqualDates,
  getExistedBasketItemIndex,
} from '../get-existed-basket-items/get-existed-basket-items';

// tslint:disable-next-line:cognitive-complexity
export function getUpdateBasketItemsRequest(
  updateItems: BasketUpdateShops,
  basketHeader: BasketHeader,
  existedBasketItems: ExtendedBasketItem[],
  deliveryShops: BasketAccountAddress[],
  updateStrategy: UpdateBasketStrategy = 'overwrite',
): BasketUpdateRequest {
  const existedBasketItemsCopy = [...existedBasketItems];
  const existedBasketItemsCount = existedBasketItemsCopy.length;
  const deliveryItems = deliveryShops.map((shop) => {
    const { accountId, addressId } = shop;
    const itemsWithDuplicates: BasketUpdateItem[] = updateItems[
      getDeliveryShopId(shop)
    ]?.flatMap((updateItem): BasketUpdateItem | BasketUpdateItem[] => {
      const duplicatedItem = findDuplicateItem(
        accountId,
        addressId,
        existedBasketItemsCopy,
        updateItem,
      );
      if (duplicatedItem) {
        const requestedQuantity =
          duplicatedItem.requestedQuantity + updateItem.requestedQuantity;
        return [
          {
            ...duplicatedItem,
            requestedQuantity,
            // let source type empty - BE will merge it with prev one
            source: { type: '' },
          } as BasketUpdateItem,
          { ...updateItem, requestedQuantity: 0 },
        ];
      }
      return updateItem;
    });

    const requestedItems =
      itemsWithDuplicates
        ?.map((updateItem) => {
          let existingItem;
          const existingItemIndex = getExistedBasketItemIndex(
            updateItem,
            accountId,
            addressId,
            existedBasketItemsCopy,
          );
          if (existingItemIndex > -1) {
            existingItem = JSON.parse(
              JSON.stringify(existedBasketItemsCopy[existingItemIndex]),
            );
            existedBasketItemsCopy.splice(existingItemIndex, 1);
          }
          const requestedQuantity =
            updateStrategy === 'overwrite'
              ? updateItem.requestedQuantity
              : updateItem.requestedQuantity +
                (existingItem?.requestedQuantity ?? 0);

          const isItemToInsertOrUpdate =
            // 1) force update from CS (update date for NOW items, etc)
            updateItem.basketItemId ||
            // 2) new item to add
            (!existingItem && requestedQuantity) ||
            // 3) requested quantity changed
            (existingItem?.requestedQuantity ?? 0) !== requestedQuantity ||
            // 4) note changed
            existingItem?.note !== updateItem.note;

          return isItemToInsertOrUpdate
            ? {
                ...updateItem,
                basketItemId:
                  updateItem.basketItemId ?? existingItem?.basketItemId,
                requestedQuantity,
                ...getMergedBasketItemSource(
                  updateItem.source,
                  updateItem.originInformation,
                  existingItem?.basketItemOrigin,
                ),
              }
            : null;
        })
        .filter(isNonNullish) ?? [];

    return {
      accountId,
      addressId,
      items: requestedItems.map(prepareBulkItems),
    };
  });

  // UI needs to send all deliveryItems for the first update to create basket accounts in basket,
  // no needs to send all deliveryItems for the second and further updates
  const hasBasketAccountsCreated = !!existedBasketItemsCount;
  const filteredDeliveryItems = hasBasketAccountsCreated
    ? deliveryItems.filter(({ items }) => !!items.length)
    : deliveryItems;

  return {
    basketHeader,
    deliveryItems: filteredDeliveryItems,
  };
}

/** Bulk (or Consignment) orders should not have requestedDeliveryDate so they
 * handled correctly by BE
 */
function prepareBulkItems(item: BasketUpdateItem) {
  if (!!item.consignmentStartDate && !!item.consignmentEndDate) {
    const { requestedDeliveryDate, ...bulkBasketItem } = item;
    return bulkBasketItem;
  }

  return item;
}

function findDuplicateItem(
  accountId: string,
  addressId: string,
  existedBasketItems: ExtendedBasketItem[],
  updateItem: BasketUpdateItem,
): ExtendedBasketItem | undefined {
  return existedBasketItems.find((item) => {
    if (
      item.accountId === accountId &&
      item.addressId === addressId &&
      updateItem.orderType === OrderType.Future &&
      updateItem?.basketItemId &&
      item.basketItemId !== updateItem.basketItemId &&
      item.productId === updateItem.productId &&
      (futureOrderEqualDates(item, updateItem) ||
        bulkOrderEqualDates(item, updateItem))
    ) {
      return item;
    }
  });
}
